import { ElementType, useState } from 'react';
import Image from 'next/image';
import YouTube, { YouTubeProps } from 'react-youtube';

import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  LinkBox,
  LinkOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useToken,
  VisuallyHidden,
} from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import {
  AccreditationColorScheme,
  AccreditationFeaturedVideoCardProps,
  accreditationGradients,
  AccreditationResourcesProps,
} from '~/features/accreditation/types';
import { trackEvent } from '~/features/analytics/track-event';
import { FormalDegreeKey } from '~/features/checkout/constants';
import { AcademicDegreeTypeIcon, BookIcon, PlayIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export function AccreditationResourcesSection({
  colorScheme = 'blue',
  formalDegreeKey,
  featuredVideoCard,
  videoQaCard,
  videos,
}: {
  colorScheme?: AccreditationColorScheme;
  formalDegreeKey?: FormalDegreeKey;
  featuredVideoCard?: AccreditationFeaturedVideoCardProps;
  videoQaCard: AccreditationResourcesProps['videoQaCard'];
  videos: Record<string, { id: string; title: string }>;
}) {
  const gradients = accreditationGradients[colorScheme];
  const [circleStrokeColor] = useToken('colors', [`${colorScheme}.300`]);
  const [activeVideo, setActiveVideo] = useState<{ id: string; title: string } | null>(null);
  const videoHeight = useBreakpointValue({ base: '240px', md: '649px' }, { fallback: 'md' });
  const portraitObjectPosition = useBreakpointValue({ base: '21px 25px', md: 'top center' }, { fallback: 'md' });

  const videoOptions: YouTubeProps['opts'] = {
    height: videoHeight,
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      autoplay: 1,
      rel: 0,
    },
    width: '100%',
  };

  function handlePlayVideo(video: { id: string; title: string }) {
    trackEvent({
      name: 'Masters Page CTA Clicked',
      type: 'click',
      label: video.title,
      additionalAttributes: {
        formal_degree_key: formalDegreeKey,
      },
    });
    setActiveVideo(video);
  }

  function handleVideoCard() {
    trackEvent({
      name: 'Masters Page CTA Clicked',
      type: 'click',
      label: 'featured_video_card_watch',
      additionalAttributes: { formal_degree_key: formalDegreeKey },
    });
  }

  return (
    <ContentContainer contentMaxWidth='content-max-width-md' mobilePadding='0'>
      <Flex
        as='section'
        bg={`${colorScheme}.50`}
        border='solid 1px'
        borderColor={`${colorScheme}.100`}
        borderLeftColor={{ base: 'transparent', md: `${colorScheme}.100` }}
        borderRadius={{ base: 0, md: '12px' }}
        borderRightColor={{ base: 'transparent', md: `${colorScheme}.100` }}
        flexDirection='column'
        gap='32px'
        isolation='isolate'
        overflow='hidden'
        p={{ base: '32px 24px', md: '48px' }}
        position='relative'
        sx={{ containerType: 'inline-size' }}
      >
        {/* Background decorative circles */}
        <Box
          bottom={0}
          display={{ base: 'none', md: 'block' }}
          pointerEvents='none'
          position='absolute'
          right={0}
          top='155px'
          w='100%'
        >
          <Box
            as='svg'
            fill='none'
            height='1092'
            viewBox='0 0 1502 1092'
            width='1502'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g opacity='0.64'>
              <circle cx='956' cy='546' opacity='0.48' r='164.5' stroke={circleStrokeColor} />
              <circle cx='956' cy='546' opacity='0.4' r='288.5' stroke={circleStrokeColor} />
              <circle cx='956' cy='546' opacity='0.32' r='419.5' stroke={circleStrokeColor} />
              <circle cx='956' cy='546' opacity='0.24' r='545.5' stroke={circleStrokeColor} />
            </g>
          </Box>
        </Box>

        {/* Header */}
        <Flex flexDirection='column' gap={{ base: '16px', md: '12px' }} position='relative'>
          <Flex alignItems='center' gap='12px'>
            <Flex alignItems='center' flexShrink={0} gap='8px'>
              <BookIcon
                color={`${colorScheme}.500`}
                height={{ base: '16px', md: '18px' }}
                width={{ base: '16px', md: '18px' }}
              />
              <Text
                color={`${colorScheme}.500`}
                fontFamily='condensed'
                fontSize='0.8125rem'
                fontWeight='500'
                letterSpacing='0.08em'
                textTransform='uppercase'
                whiteSpace='nowrap'
              >
                resources
              </Text>
            </Flex>
            <Box bg={`${colorScheme}.500`} flex={1} h='0.5px' />
          </Flex>
          <Heading
            as='h2'
            color='grey.900'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            letterSpacing='-0.01em'
            lineHeight={{ base: '1.15', md: '1.1' }}
          >
            Helpful resources to get you started
          </Heading>
          <Text color='grey.500' fontSize={{ base: '0.875rem', md: '1rem' }} lineHeight={{ base: '1.4', md: '1.5' }}>
            Explore the program structure, academic focus, and student support designed to help you succeed.
          </Text>
        </Flex>

        {/* Resource cards */}
        <Grid
          gap='24px'
          position='relative'
          templateColumns='1fr'
          sx={{
            '@container (min-width: 922px)': {
              gridTemplateColumns: '1fr 1fr',
            },
          }}
        >
          {/* Card 1: Video Q&A */}
          <Box
            as='article'
            bg={`${colorScheme}.100`}
            borderRadius={{ base: '6px', md: '8px' }}
            minHeight={{ base: '220px', md: '260px' }}
            overflow='hidden'
            p={{ base: '18px', md: '24px' }}
            position='relative'
          >
            <Box bottom={{ base: '-44px', md: '0px' }} pointerEvents='none' position='absolute' right='-88px'>
              <Image
                alt=''
                height={282}
                loading='lazy'
                quality={100}
                sizes='100vw'
                src='/images/accreditation/resources-video-thumbnail.png'
                width={315}
                style={{
                  filter: 'drop-shadow(10px 7px 27px rgba(15, 35, 56, 0.35))',
                }}
              />
            </Box>
            <Flex flexDirection='column' gap='16px' mr='34%' position='relative'>
              <ResourceTag colorScheme={colorScheme} label={videoQaCard.tag} variant='light' />
              <Flex flexDirection='column' gap='16px'>
                <Heading
                  as='h3'
                  color={`${colorScheme}.900`}
                  fontSize={{ base: '1.75rem', md: '2.25rem' }}
                  fontWeight='600'
                  letterSpacing='-0.36px'
                  lineHeight={{ base: '1.15', md: '1.1' }}
                >
                  {videoQaCard.heading}
                </Heading>
                <ResourceButton label='Watch Video' onClick={() => videos.videoQA && handlePlayVideo(videos.videoQA)} />
              </Flex>
            </Flex>
          </Box>

          {/* Card 2: Featured video card (speaker portrait + replay link) */}
          {featuredVideoCard ? (
            <Flex
              as={LinkBox}
              background={featuredVideoCard.background ?? 'linear-gradient(-63.61deg, #540916 0%, #0A0B0F 100%)'}
              borderRadius={{ base: '6px', md: '8px' }}
              minHeight={{ base: '220px', md: '260px' }}
              overflow='hidden'
              p={{ base: '18px', md: '24px' }}
              position='relative'
              onClick={handleVideoCard}
            >
              {/* Background decorative circles */}
              <Box
                left={{ base: '-395px', md: '-540px' }}
                opacity={0.32}
                pointerEvents='none'
                position='absolute'
                top={{ base: '7px', md: '-31px' }}
              >
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '241px', md: '330px' }}
                  left={{ base: '593px', md: '811px' }}
                  opacity={0.48}
                  position='absolute'
                  top={{ base: '278px', md: '381px' }}
                  w={{ base: '241px', md: '330px' }}
                />
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '422px', md: '578px' }}
                  left={{ base: '502px', md: '687px' }}
                  opacity={0.4}
                  position='absolute'
                  top={{ base: '188px', md: '257px' }}
                  w={{ base: '422px', md: '578px' }}
                />
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '614px', md: '840px' }}
                  left={{ base: '406px', md: '556px' }}
                  opacity={0.32}
                  position='absolute'
                  top={{ base: '92px', md: '126px' }}
                  w={{ base: '614px', md: '840px' }}
                />
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '798px', md: '1092px' }}
                  left={{ base: '314px', md: '430px' }}
                  opacity={0.24}
                  position='absolute'
                  top='0px'
                  w={{ base: '798px', md: '1092px' }}
                />
              </Box>

              {/* Portrait — absolute, bottom-right */}
              <Box
                bottom='0'
                h={{ base: '259px' }}
                overflow='hidden'
                pointerEvents='none'
                position='absolute'
                right={{ base: '-18px', md: '-30px' }}
                w={{ base: '234px' }}
                zIndex={2}
              >
                <Box h='100%' position='relative' w='100%'>
                  <Image
                    alt=''
                    sizes='234px'
                    src={featuredVideoCard.portraitPath}
                    style={{ objectFit: 'cover', objectPosition: portraitObjectPosition }}
                    fill
                  />
                </Box>
              </Box>

              {/* Content */}
              <Flex flexDirection='column' justifyContent='space-between' w='100%' zIndex={3}>
                <ResourceTag colorScheme={colorScheme} label='webinar' variant='dark' />
                <Heading
                  as='h3'
                  color='white'
                  fontSize={{ base: '1.75rem', md: '2.125rem' }}
                  fontWeight='600'
                  lineHeight='1.1'
                  maxWidth='74%'
                  mt={{ base: '8px', md: 0 }}
                >
                  <LinkOverlay href={featuredVideoCard.href}>{featuredVideoCard.title}</LinkOverlay>
                </Heading>
                {featuredVideoCard.description && (
                  <Text
                    color='white'
                    fontSize='1rem'
                    lineHeight='1.5'
                    maxWidth={{ base: '75%', md: '50%' }}
                    mt={{ base: '8px', md: 0 }}
                  >
                    {featuredVideoCard.description}
                  </Text>
                )}
                <Flex alignItems='flex-end' justifyContent='space-between' mt={{ base: '8px', md: 0 }}>
                  <ResourceButton href={featuredVideoCard.href} icon={PlayIcon} label='Watch Video' />
                  {featuredVideoCard.speakerLabel && (
                    <Box bg='grey.900' borderRadius='4px' flexShrink={0} ml='8px' px='12px' py='6px'>
                      <Text color='gold.300' fontSize='0.8125rem' fontWeight='500' lineHeight='1.4' whiteSpace='nowrap'>
                        {featuredVideoCard.speakerLabel}
                      </Text>
                    </Box>
                  )}
                </Flex>
              </Flex>
            </Flex>
          ) : videos.fullWebinar ? (
            <Flex
              as='article'
              bgGradient={gradients.resourcesCardBg}
              borderRadius={{ base: '6px', md: '8px' }}
              minHeight={{ base: '220px', md: '260px' }}
              overflow='hidden'
              p={{ base: '18px', md: '24px' }}
              position='relative'
            >
              {/* Background decorative circles */}
              <Box
                left={{ base: '-395px', md: '-540px' }}
                opacity={0.32}
                pointerEvents='none'
                position='absolute'
                top={{ base: '7px', md: '-31px' }}
              >
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '241px', md: '330px' }}
                  left={{ base: '593px', md: '811px' }}
                  opacity={0.48}
                  position='absolute'
                  top={{ base: '278px', md: '381px' }}
                  w={{ base: '241px', md: '330px' }}
                />
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '422px', md: '578px' }}
                  left={{ base: '502px', md: '687px' }}
                  opacity={0.4}
                  position='absolute'
                  top={{ base: '188px', md: '257px' }}
                  w={{ base: '422px', md: '578px' }}
                />
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '614px', md: '840px' }}
                  left={{ base: '406px', md: '556px' }}
                  opacity={0.32}
                  position='absolute'
                  top={{ base: '92px', md: '126px' }}
                  w={{ base: '614px', md: '840px' }}
                />
                <Box
                  border='1px solid'
                  borderColor={`${colorScheme}.300`}
                  borderRadius='50%'
                  h={{ base: '798px', md: '1092px' }}
                  left={{ base: '314px', md: '430px' }}
                  opacity={0.24}
                  position='absolute'
                  top='0px'
                  w={{ base: '798px', md: '1092px' }}
                />
              </Box>

              <Flex flexDirection='row' justifyContent='space-between' position='relative' w='100%'>
                {/* Left content */}
                <Flex alignSelf='stretch' flex={1} flexDirection='column' gap={4}>
                  <ResourceTag colorScheme={colorScheme} label='full webinar' variant='dark' />
                  <Flex flexDirection='column' gap='16px'>
                    <Heading
                      as='h3'
                      color='white'
                      fontSize={{ base: '1.75rem', md: '2.25rem' }}
                      fontWeight='600'
                      letterSpacing='-0.36px'
                      lineHeight={{ base: '1.15', md: '1.1' }}
                    >
                      <Box as='span' color='gold.300'>
                        Master&apos;s in AI:
                      </Box>{' '}
                      Everything you need to know
                    </Heading>
                    <ResourceButton label='Watch Webinar' onClick={() => handlePlayVideo(videos.fullWebinar)} />
                  </Flex>
                </Flex>

                {/* Right: pic grid */}
                <Box alignSelf='flex-end' flexShrink={0} ml={{ base: '16px', md: '24px' }}>
                  <Image
                    alt=''
                    height={176}
                    loading='lazy'
                    quality={100}
                    sizes='100vw'
                    src='/images/accreditation/resources-pic-grid.png'
                    width={132}
                  />
                </Box>
              </Flex>
            </Flex>
          ) : null}
        </Grid>

        <Modal isOpen={!!activeVideo} size='6xl' isCentered onClose={() => setActiveVideo(null)}>
          <ModalOverlay backdropFilter='blur(10px) brightness(0.25)' />
          <ModalContent bg='grey.900' color='white'>
            <VisuallyHidden as={ModalHeader}>Resource Video</VisuallyHidden>
            <ModalCloseButton color='white' />
            <ModalBody p={0}>
              {activeVideo && <YouTube opts={videoOptions} title={activeVideo.title} videoId={activeVideo.id} />}
            </ModalBody>
          </ModalContent>
        </Modal>
      </Flex>
    </ContentContainer>
  );
}

function ResourceTag({
  colorScheme = 'blue',
  label,
  variant,
}: {
  colorScheme?: AccreditationColorScheme;
  label: string;
  variant: 'light' | 'dark';
}) {
  return (
    <Flex
      alignItems='center'
      bg={variant === 'light' ? 'white' : `${colorScheme}.900`}
      borderRadius={{ base: '3px', md: '4px' }}
      gap={{ base: '6px', md: '8px' }}
      p={{ base: '6px 8px 5px 6px', md: '6px 10px 6px 8px' }}
      w='fit-content'
    >
      <AcademicDegreeTypeIcon
        color={variant === 'light' ? 'gold.500' : `${colorScheme}.300`}
        height={{ base: '10px', md: '12px' }}
        width={{ base: '10px', md: '12px' }}
      />
      <Text
        color={variant === 'light' ? 'gold.500' : `${colorScheme}.300`}
        fontFamily='condensed'
        fontSize={{ base: '0.75rem', md: '0.8125rem' }}
        fontWeight='500'
        letterSpacing='0.08em'
        lineHeight='1'
        textTransform='uppercase'
      >
        {label}
      </Text>
    </Flex>
  );
}

function ResourceButton({
  href,
  icon: Icon = PlayIcon,
  label,
  onClick,
}: {
  href?: string;
  icon?: ElementType;
  label: string;
  onClick?: () => void;
}) {
  return (
    <Button
      _hover={{ bg: 'blue.600' }}
      as={href ? 'a' : undefined}
      bg='blue.500'
      borderRadius='4px'
      color='white'
      fontSize='0.75rem'
      fontWeight='400'
      h='auto'
      href={href}
      leftIcon={<Icon height='14px' width='14px' />}
      lineHeight='1.4'
      p='10px 16px 10px 12px'
      rel={href ? 'noopener noreferrer' : undefined}
      target={href ? '_blank' : undefined}
      w='fit-content'
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
